import { ReelBrand } from "@/components/admin-ui/reel-brand"
import { useCallback, useEffect, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  Video,
  Users,
  Megaphone,
  ShieldCheck,
  DollarSign,
  Settings,
  Lock,
  UserCircle,
  ShoppingBag,
  Building2,
  Gift,
  ChevronRight,
  PanelLeftClose,
  LogOut,
  Moon,
  Sun,
  MessageSquareWarning,
  UserCheck,
  BadgeCheck,
  Flag,
  Star,
  ClipboardCheck,
} from "lucide-react"
import { artistHubEnabled } from "@/services/firebase/artistHub"
import { useArtistFlags } from "@/hooks/useArtistFlags"
import { ArtistFlagGate, flagAllows } from "@/lib/artistFlags"
import { useAuth } from "@/hooks/useAuth"
import { useTheme } from "@/hooks/useTheme"
import { usePermissions } from "@/hooks/usePermissions"
import { getVersionInfo, getEnvironment } from "@/utils/version"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

/* ── Nav types ── */

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  adminOnly?: boolean
  /** U2: shown only while the RC flag is on (fans = artist_fans_enabled; dm = fans && artist_dm_enabled). */
  flag?: ArtistFlagGate
}

interface NavGroup {
  key: string
  label: string
  items: NavItem[]
  defaultOpen?: boolean
}

/* ── Nav data ── */

const NAV_GROUPS: NavGroup[] = [
  {
    key: "content",
    label: "內容",
    defaultOpen: true,
    items: [
      { path: "/dramas", label: "內容管理", icon: Video },
    ],
  },
  {
    // U4: 藝人 (entity) group. 藝人管理 and 動態檢舉 show in every environment (as before); the rest stay behind
    // artistHubEnabled (sandbox until rollout), and the fan/DM entries also follow Remote Config.
    key: "artists",
    label: "藝人",
    defaultOpen: true,
    items: [
      { path: "/actors", label: "藝人管理", icon: UserCircle },
      ...(artistHubEnabled
        ? [
            { path: "/artists/featured", label: "精選藝人", icon: Star },
            { path: "/artists/review", label: "內容待審", icon: ClipboardCheck },
            { path: "/creators/applications", label: "創作者申請", icon: UserCheck },
          ]
        : []),
      // Q-02: the UGC report queue works in every environment once artistHub is deployed (not behind artistHubEnabled).
      { path: "/moderation/artist", label: "動態檢舉", icon: Flag },
      ...(artistHubEnabled
        ? [
            { path: "/moderation/dm", label: "私訊審核", icon: MessageSquareWarning, flag: "dm" as const },
            { path: "/creators/fan-pass-requests", label: "月卡申請", icon: BadgeCheck, flag: "fans" as const },
          ]
        : []),
    ],
  },
  {
    key: "users",
    label: "用戶管理",
    defaultOpen: true,
    items: [
      { path: "/users", label: "使用者管理", icon: Users },
      { path: "/moderation/comments", label: "評論管理", icon: ShieldCheck },
    ],
  },
  {
    key: "operations",
    label: "運營",
    defaultOpen: true,
    items: [
      { path: "/announcements", label: "公告管理", icon: Megaphone },
      { path: "/gift-codes", label: "禮物代碼", icon: Gift },
    ],
  },
  {
    key: "finance",
    label: "財務",
    defaultOpen: true,
    items: [
      { path: "/financial", label: "財務總覽", icon: DollarSign },
      { path: "/companies", label: "製作公司", icon: Building2 },
    ],
  },
  {
    key: "links",
    label: "連結",
    items: [
      { path: "/products", label: "商品管理", icon: ShoppingBag },
    ],
  },
]

/* ── localStorage key for group open state ── */
const GROUPS_STORAGE_KEY = "cms-sidebar-groups"

function loadGroupState(): Record<string, boolean> {
  // Defaults first, then the saved state — a group added after the state was saved (e.g. "artists") gets its default.
  const state: Record<string, boolean> = {}
  NAV_GROUPS.forEach((g) => {
    state[g.key] = g.defaultOpen ?? false
  })
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY)
    const saved = raw ? (JSON.parse(raw) as unknown) : null
    if (saved && typeof saved === "object") {
      for (const [key, value] of Object.entries(saved as Record<string, unknown>)) {
        if (key in state && typeof value === "boolean") state[key] = value
      }
    }
  } catch {
    /* ignore */
  }
  return state
}

function saveGroupState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore */
  }
}

/* ── Component ── */

export function AdminSidebar() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { isAdmin } = usePermissions()
  const { state, toggleSidebar } = useSidebar()
  const artistFlags = useArtistFlags()

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const [versionInfo, setVersionInfo] = useState<{ version: string; updatedAt: string } | null>(null)
  const environment = getEnvironment()

  useEffect(() => {
    setOpenGroups(loadGroupState())
  }, [])

  // Load version info
  useEffect(() => {
    getVersionInfo().then((info) => {
      if (info) {
        setVersionInfo({ version: info.version, updatedAt: info.updatedAt })
      }
    })
  }, [])

  const toggleGroup = useCallback((key: string) => {
    setOpenGroups((prev) => {
      const current = prev[key] ?? NAV_GROUPS.find((g) => g.key === key)?.defaultOpen ?? false
      const next = { ...prev, [key]: !current }
      saveGroupState(next)
      return next
    })
  }, [])

  const isActive = useCallback(
    (href: string) => pathname === href || pathname.startsWith(href + "/"),
    [pathname],
  )

  const initial = user?.displayName?.[0] || user?.email?.[0] || "U"
  const displayName = user?.displayName || (user?.email ? user.email.split("@")[0] : "User")

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error("Sign out error:", error)
    }
  }

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      {/* ── Header ── */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              tooltip="Reel+ 管理後台"
              onClick={() => {
                if (state === "collapsed") {
                  toggleSidebar()
                } else {
                  navigate("/dramas")
                }
              }}
              className="cursor-pointer"
            >
              <img
                src="/logo.png"
                alt=""
                className="size-8 shrink-0 rounded-lg"
              />
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="inline-flex items-center gap-2 font-semibold"><ReelBrand showMark={false} /><span>後台</span></span>
                {versionInfo && (
                  <div className="text-[10px] text-sidebar-foreground/50">
                    {environment.name}・{versionInfo.version}
                  </div>
                )}
              </div>
            </SidebarMenuButton>
            {state === "expanded" && (
              <button
                onClick={toggleSidebar}
                className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-md p-1 text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground md:flex"
                title="收合側邊欄"
              >
                <PanelLeftClose className="h-3.5 w-3.5" />
              </button>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* ── Content ── */}
      <SidebarContent>
        {NAV_GROUPS.map((group) => {
          const visible = group.items.filter((item) => {
            if (item.adminOnly && !isAdmin) return false
            if (!flagAllows(item.flag, artistFlags)) return false
            return true
          })
          if (visible.length === 0) return null

          const groupOpen = openGroups[group.key] ?? group.defaultOpen ?? false

          return (
            <Collapsible
              key={group.key}
              open={groupOpen}
              onOpenChange={() => toggleGroup(group.key)}
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center gap-1">
                    <span className="flex-1 text-left">{group.label}</span>
                    <ChevronRight className="h-3 w-3 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {visible.map((item) => (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive(item.path)}
                            tooltip={item.label}
                          >
                            <Link to={item.path}>
                              <item.icon />
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          )
        })}
      </SidebarContent>

      {/* ── Footer ── */}
      <SidebarFooter>
        {/* User */}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" tooltip={displayName}>
                  <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {initial}
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{displayName}</span>
                    <span className="truncate text-xs text-sidebar-foreground/60">
                      {user?.email}
                    </span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                side="top"
                align="start"
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
              >
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                    {initial}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                    {user?.isAdmin && (
                      <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Lock className="h-2.5 w-2.5" /> 管理員
                      </span>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    系統設定
                  </Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/iam" className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      IAM 管理
                    </Link>
                  </DropdownMenuItem>
                )}
                {/* Theme toggle 已隱藏，CMS 鎖定 light mode（見 useTheme.ts） */}
                {false && (
                  <DropdownMenuItem onClick={toggleTheme}>
                    {theme === "light" ? (
                      <Moon className="h-4 w-4" />
                    ) : (
                      <Sun className="h-4 w-4" />
                    )}
                    {theme === "light" ? "深色模式" : "淺色模式"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  登出
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
